import { formatCurrency } from '@if/core/utils'
import {
  Caption,
  DataField,
  FieldGrid,
  FieldLabel,
  Icon,
  Placeholder,
  StrongValue,
  useCollapse,
  useDialog,
} from '@if/mui'
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
} from '@mui/material'
import {
  PromoPlanHistoryEntryDto,
  useGetCustomBuydownHistory,
  useGetExpiredPromoPlans,
} from '@src/data/api/merchants-api/merchants-api'
import { History } from 'lucide-react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

const formatPercent = (value: number) => `${(value * 100).toFixed(2)}%`

export const formatUtcDateTime = (date: string) => new Date(date).toISOString().slice(0, 16).replace('T', ', ')

const findRate = (entry: PromoPlanHistoryEntryDto, term: number) =>
  entry.promoTermRates.find((rate) => rate.termMonths === term)?.promoRate ?? null

const formatRate = (rate: number | null) => (rate === null ? '' : formatPercent(rate))

interface HistoryEntryProps {
  entry: PromoPlanHistoryEntryDto
  previous: PromoPlanHistoryEntryDto | undefined
  terms: number[]
  isCurrent: boolean
}

const HistoryEntry = ({ entry, previous, terms, isCurrent }: HistoryEntryProps) => {
  const { t } = useTranslation()

  const formatMaxFinancing = (amount: number | null) =>
    amount ? formatCurrency(amount) : t('merchant.customBuydown.noMaximum')

  const previousIfChanged = <T,>(pick: (e: PromoPlanHistoryEntryDto) => T, format: (value: T) => string) =>
    previous && pick(previous) !== pick(entry) ? format(pick(previous)) : undefined

  return (
    <Stack spacing={1.5} sx={{ px: 3, py: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
        <Stack>
          <StrongValue>
            {t('merchant.customBuydown.history.utcDateTime', { date: formatUtcDateTime(entry.effectiveOn) })}
          </StrongValue>
          {entry.modifiedByUserFullname && (
            <Caption>{t('merchant.customBuydown.history.modifiedBy', { name: entry.modifiedByUserFullname })}</Caption>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          {!previous && <Caption>{t('merchant.customBuydown.history.firstVersion')}</Caption>}
          {isCurrent && <Chip size="small" label={t('merchant.customBuydown.history.current')} />}
        </Stack>
      </Stack>

      <FieldGrid columns={{ xs: 2, sm: 3 }}>
        <DataField label={t('merchant.customBuydown.history.startDate')} value={formatUtcDateTime(entry.activeFrom)} />
        <DataField
          label={t('merchant.customBuydown.history.endDate')}
          value={entry.expiresOn ? formatUtcDateTime(entry.expiresOn) : t('merchant.customBuydown.history.noEndDate')}
        />
        <DataField
          label={t('merchant.customBuydown.contribution')}
          value={formatPercent(entry.merchantFeeRate)}
          previousValue={previousIfChanged((e) => e.merchantFeeRate, formatPercent)}
        />
        <DataField
          label={t('merchant.customBuydown.adminFee')}
          value={formatPercent(entry.borrowerFeeRate)}
          previousValue={previousIfChanged((e) => e.borrowerFeeRate, formatPercent)}
        />
        <DataField
          label={t('merchant.customBuydown.maxFinancingAmount')}
          value={formatMaxFinancing(entry.maxFinancingAmount)}
          previousValue={previousIfChanged((e) => e.maxFinancingAmount || null, formatMaxFinancing)}
        />
        <DataField
          label={t('merchant.customBuydown.rebate')}
          value={formatPercent(entry.appliedRebate)}
          previousValue={previousIfChanged((e) => e.appliedRebate, formatPercent)}
        />
      </FieldGrid>

      <Stack spacing={1}>
        <FieldLabel>{t('merchant.customBuydown.history.rateByTerm')}</FieldLabel>
        <FieldGrid columns={{ xs: 3, sm: terms.length }}>
          {terms.map((term) => (
            <DataField
              key={term}
              label={t('merchant.customBuydown.history.termShort', { term })}
              value={formatRate(findRate(entry, term))}
              previousValue={previousIfChanged((e) => findRate(e, term), formatRate)}
            />
          ))}
        </FieldGrid>
      </Stack>
    </Stack>
  )
}

interface HistoryDialogProps {
  merchantId: string
  planKey: string
  planCode: string
  isCurrentPlan: boolean
}

const CustomBuydownHistoryList = ({ merchantId, planKey, isCurrentPlan }: HistoryDialogProps) => {
  const { t } = useTranslation()
  const [history, isLoading] = useGetCustomBuydownHistory(merchantId, planKey)

  if (isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress />
      </Stack>
    )
  }

  if (!history?.length) {
    return (
      <Stack sx={{ px: 3, py: 2 }}>
        <Placeholder>{t('merchant.customBuydown.history.empty')}</Placeholder>
      </Stack>
    )
  }

  const terms = [...new Set(history.flatMap((entry) => entry.promoTermRates.map((rate) => rate.termMonths)))].sort(
    (a, b) => a - b,
  )

  return (
    <Box>
      {history.map((entry, index) => (
        <Fragment key={entry.id}>
          {index > 0 && <Divider />}
          <HistoryEntry
            entry={entry}
            previous={history[index + 1]}
            terms={terms}
            isCurrent={isCurrentPlan && index === 0}
          />
        </Fragment>
      ))}
    </Box>
  )
}

const SavedVersionsCount = ({ merchantId, planKey }: HistoryDialogProps) => {
  const { t } = useTranslation()
  const [history] = useGetCustomBuydownHistory(merchantId, planKey)
  if (!history?.length) return null
  return <Caption>{t('merchant.customBuydown.history.savedVersions', { count: history.length })}</Caption>
}

export const CustomBuydownHistoryButton = (props: HistoryDialogProps) => {
  const { t } = useTranslation()
  const dialog = useDialog<HistoryDialogProps>({
    icon: History,
    title: t('merchant.customBuydown.history.title'),
    subtitle: (ctx) => t('merchant.customBuydown.history.subtitle', { planCode: ctx.props.planCode }),
    content: (ctx) => <CustomBuydownHistoryList {...ctx.props} />,
    additionalActions: (ctx) => <SavedVersionsCount {...ctx.props} />,
    edgeToEdge: true,
    hideConfirm: true,
    cancelLabel: t('merchant.customBuydown.history.close'),
    maxWidth: 'md',
    fullWidth: true,
  })

  return (
    <Button
      variant="outlined"
      size="small"
      startIcon={<Icon icon={History} size="sm" />}
      onClick={() => void dialog.open(props)}
    >
      {t('merchant.customBuydown.history.action')}
    </Button>
  )
}

export const ExpiredCustomBuydownPlans = ({ merchantId }: { merchantId: string }) => {
  const { t } = useTranslation()
  const [expiredPlans] = useGetExpiredPromoPlans(merchantId)
  const { icon, triggerProps, regionProps } = useCollapse()

  if (!expiredPlans?.length) return null

  return (
    <Card variant="outlined">
      <CardActionArea {...triggerProps}>
        <CardHeader
          title={t('merchant.customBuydown.history.expiredPlansTitle')}
          subheader={t('merchant.customBuydown.history.expiredPlansDescription')}
          action={icon}
          slotProps={{
            title: { variant: 'subtitle1', color: 'primary' },
            subheader: { variant: 'body2' },
            action: { sx: { alignSelf: 'center', m: 0 } },
          }}
        />
      </CardActionArea>
      <Collapse {...regionProps} role="region">
        <Divider />
        <List disablePadding>
          {expiredPlans.map((plan, index) => (
            <Fragment key={plan.planKey}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                secondaryAction={
                  <CustomBuydownHistoryButton
                    merchantId={merchantId}
                    planKey={plan.planKey}
                    planCode={plan.label}
                    isCurrentPlan={false}
                  />
                }
              >
                <ListItemText
                  primary={plan.label}
                  secondary={t('merchant.customBuydown.history.endedOn', { date: formatUtcDateTime(plan.expiresOn) })}
                />
              </ListItem>
            </Fragment>
          ))}
        </List>
      </Collapse>
    </Card>
  )
}
